import React, { useMemo } from 'react';
import { QuoteState, Country } from '../types';

interface QuotePreviewProps {
  data: QuoteState;
  country?: Country;
}

const THEMES = {
  [Country.TANZANIA]: {
    primary: 'text-emerald-700',
    bgPrimary: 'bg-emerald-700',
    bgSecondary: 'bg-emerald-50',
    borderPrimary: 'border-emerald-700',
    borderSecondary: 'border-emerald-200',
    accent: 'text-emerald-500',
    totalBg: 'bg-emerald-900',
    highlight: 'text-emerald-300'
  },
  [Country.UGANDA]: {
    primary: 'text-amber-900',
    bgPrimary: 'bg-amber-900',
    bgSecondary: 'bg-amber-50',
    borderPrimary: 'border-amber-900',
    borderSecondary: 'border-amber-200',
    accent: 'text-amber-700',
    totalBg: 'bg-stone-900',
    highlight: 'text-amber-200'
  },
  [Country.KENYA]: {
    primary: 'text-sky-700',
    bgPrimary: 'bg-sky-700',
    bgSecondary: 'bg-sky-50',
    borderPrimary: 'border-sky-700',
    borderSecondary: 'border-sky-200',
    accent: 'text-sky-500',
    totalBg: 'bg-sky-900',
    highlight: 'text-sky-300'
  }
};

export const QuotePreview: React.FC<QuotePreviewProps> = ({ data, country = Country.TANZANIA }) => {
  const theme = THEMES[country] || THEMES[Country.TANZANIA];
  const taxIdLabel = country === Country.KENYA ? 'PIN' : 'TIN';
  const isKenya = country === Country.KENYA;
  const isTanzania = country === Country.TANZANIA;

  const activeItems = useMemo(() => {
    return data.items.filter(item => item.name && item.name.trim() !== '');
  }, [data.items]);

  const { subtotal, taxAmount, total } = useMemo(() => {
    if (isKenya) {
      const inclusiveTotal = activeItems.reduce((acc, item) => {
        const effectiveRate = Number(item.price || 0) * (item.drumWeight || 1);
        return acc + (Number(item.quantity || 0) * effectiveRate);
      }, 0);
      const taxRateDec = Number(data.details.taxRate || 0) / 100;
      const exclusiveSubtotal = inclusiveTotal / (1 + taxRateDec);
      const vat = inclusiveTotal - exclusiveSubtotal;
      return { subtotal: exclusiveSubtotal, taxAmount: vat, total: inclusiveTotal };
    } else {
      const exclusiveSubtotal = activeItems.reduce((acc, item) => acc + (Number(item.quantity || 0) * Number(item.price || 0)), 0);
      const vat = exclusiveSubtotal * (Number(data.details.taxRate || 0) / 100);
      return { subtotal: exclusiveSubtotal, taxAmount: vat, total: exclusiveSubtotal + vat };
    }
  }, [activeItems, data.details.taxRate, isKenya]);

  const formatCurrency = (amount: any) => {
    const num = Number(amount);
    if (isNaN(num)) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).replace(/ /g, ' ');
    } catch (e) {
      return dateString;
    }
  };

  const tableDataCellStyle = "text-[13px] font-bold text-slate-900 tracking-tight";
  const termLabelStyle = `text-[8px] font-black ${theme.accent} uppercase block mb-0.5 tracking-widest`;
  const termValueStyle = "text-[11px] font-black text-slate-900 uppercase tracking-tight leading-tight";
  
  const isVatNA = data.details.incoterms.startsWith('C&F') || data.details.incoterms.startsWith('D.D.U');

  return (
    <div 
      className="bg-white text-slate-800 font-sans relative flex flex-col print:shadow-none shadow-2xl print-area"
      style={{ 
        width: '210mm', 
        height: '297mm',
        minHeight: '297mm',
        margin: '0 auto',
        padding: '0',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        overflow: 'hidden'
      }}
    >
        <div className="p-[10mm] flex-1 flex flex-col h-full overflow-hidden">
            {/* Header Identity */}
            <div className={`flex justify-between items-start border-b-4 ${theme.borderPrimary} pb-4 mb-4`}>
                <div className="flex flex-1 gap-6 items-start">
                    <div className="w-24 h-24 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden shadow-inner mt-0">
                        {data.business.logoUrl ? (
                            <img 
                                key={data.business.logoUrl}
                                src={data.business.logoUrl} 
                                alt="Company Logo" 
                                crossOrigin="anonymous"
                                className="w-full h-full object-contain p-2 company-logo-img"
                                loading="eager"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const placeholder = document.createElement('div');
                                    placeholder.className = "text-[8px] font-black text-slate-300 uppercase tracking-widest text-center px-2";
                                    placeholder.innerText = "Logo Unreachable";
                                    target.parentElement?.appendChild(placeholder);
                                }}
                            />
                        ) : (
                            <div className="text-[8px] font-black text-slate-300 uppercase tracking-widest text-center px-2">
                                Logo Area
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <h1 className={`text-2xl font-black ${theme.primary} uppercase tracking-tighter leading-none mb-2 mt-0`}>
                            {data.business.name}
                        </h1>
                        <div className="text-slate-500 space-y-1 font-medium leading-tight max-w-md">
                            <p className="whitespace-pre-line text-[11px] text-slate-600 font-bold">
                                {data.business.address}
                            </p>
                            <div className="flex flex-wrap gap-x-4 mt-1 text-[10px]">
                                {data.business.phone && (
                                    <p>Tel: <span className="text-slate-800 font-black">{data.business.phone}</span></p>
                                )}
                                {data.business.email && (
                                    <p>Email: <span className="text-slate-800 font-black lowercase">{data.business.email}</span></p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="text-right mt-0">
                    <div className={`bg-slate-50 border ${theme.borderSecondary} rounded-xl p-2 shadow-sm min-w-[160px]`}>
                        <p className="text-[11px] font-black text-slate-500 uppercase tracking-tight flex justify-between gap-3">
                            <span>{taxIdLabel}:</span>
                            <span className="text-slate-900 font-black">{data.business.tin}</span>
                        </p>
                        {data.business.vrn && (
                            <p className="text-[11px] font-black text-slate-500 uppercase tracking-tight border-t border-slate-200 pt-1 flex justify-between gap-3 mt-1">
                                <span>VRN:</span>
                                <span className="text-slate-900 font-black">{data.business.vrn}</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Document Details & Buyer */}
            <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                    <div className={`${theme.bgPrimary} text-white px-5 py-3 rounded-2xl mb-2 shadow-md w-fit`}>
                        <h2 className={`text-[9px] font-black uppercase tracking-[0.3em] mb-1 opacity-90`}>Proforma Invoice</h2>
                        <p className="text-xl font-bold tracking-tight">{data.details.number}</p>
                    </div>
                </div>
                <div className="text-right space-y-1 w-48">
                    <div className="flex justify-between items-baseline gap-3">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">DATE</span>
                        <span className="text-[11px] font-black text-slate-900">{formatDate(data.details.date)}</span>
                    </div>
                    <div className="flex justify-between items-baseline gap-3 border-t border-slate-100 pt-1">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">VALID UNTIL</span>
                        <span className="text-[11px] font-black text-red-600">{formatDate(data.details.priceValidity)}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                    <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest block mb-1">BUYER</span>
                    <p className="font-bold text-slate-900 text-[13px] mb-1 uppercase tracking-tight leading-none">{data.client.name}</p>
                    <p className="text-slate-800 text-[13px] whitespace-pre-line leading-relaxed mb-2 font-bold uppercase">
                        {data.client.address}
                    </p>
                    <div className="text-slate-600 text-[11px] font-bold space-y-0.5 border-t border-slate-100 pt-2">
                        {data.client.phone && <p>Tel: <span className="text-slate-900">{data.client.phone}</span></p>}
                        {data.client.email && <p>Email: <span className="text-slate-900 lowercase">{data.client.email}</span></p>}
                        {data.client.tin && <p>{taxIdLabel}: <span className="text-slate-900">{data.client.tin}</span></p>}
                        {data.client.vrn && <p>VRN: <span className="text-slate-900">{data.client.vrn}</span></p>}
                    </div>
                </div>
                <div className={`${theme.bgSecondary} rounded-xl p-3 border ${theme.borderSecondary}`}>
                    <div className="grid grid-cols-1 gap-2">
                        <div>
                            <span className={termLabelStyle}>PAYMENT METHOD</span>
                            <p className={termValueStyle}>{data.details.paymentTerms}</p>
                        </div>
                        <div className={`pt-1.5 border-t ${theme.borderSecondary} flex flex-col gap-2`}>
                            {!isKenya && (
                                <div>
                                    <span className={termLabelStyle}>INCOTERMS</span>
                                    <p className={termValueStyle}>{data.details.incoterms}</p>
                                </div>
                            )}
                            <div>
                                <span className={termLabelStyle}>DELIVERY TERMS</span>
                                <p className={termValueStyle}>{data.details.deliveryTerms}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Items Table */}
            <div className="mb-2 min-h-[140px]">
                <table className="w-full">
                    <thead>
                        <tr className={`${theme.bgPrimary} text-white text-[10px] uppercase font-black tracking-[0.15em]`}>
                            <th className="py-2 px-3 text-left rounded-tl-lg w-10">SR.</th>
                            <th className="py-2 px-2 text-left">DESCRIPTION OF GOODS</th>
                            <th className="py-2 px-2 text-center w-20">QTY</th>
                            <th className="py-2 px-2 text-right w-28">RATE ({data.details.currency})</th>
                            <th className="py-2 px-3 text-right rounded-tr-lg w-32">AMOUNT</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {activeItems.map((item, index) => {
                            const rateVal = Number(item.price || 0);
                            const qtyVal = Number(item.quantity || 0);
                            const effectiveRate = isKenya ? (rateVal * (item.drumWeight || 1)) : rateVal;
                            const effectiveAmount = qtyVal * effectiveRate;
                            
                            return (
                                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className={`py-3 px-3 align-top font-black text-slate-400 text-[11px]`}>{index + 1}</td>
                                    <td className="py-3 px-2 align-top">
                                        <p className={`${tableDataCellStyle} uppercase mb-0.5 whitespace-pre-wrap`}>{item.name}</p>
                                        <p className="text-[11px] text-slate-500 leading-tight font-bold italic whitespace-pre-wrap">{item.description}</p>
                                    </td>
                                    <td className={`py-3 px-2 align-top text-center ${tableDataCellStyle}`}>
                                        {qtyVal} <span className="ml-0.5 opacity-50">{item.unit}</span>
                                    </td>
                                    <td className={`py-3 px-2 align-top text-right ${tableDataCellStyle}`}>
                                        {formatCurrency(effectiveRate)}
                                    </td>
                                    <td className={`py-3 px-3 align-top text-right ${tableDataCellStyle} text-black`}>
                                        {formatCurrency(effectiveAmount)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Totals & Bank Details */}
            <div className="mt-2 space-y-2">
                <div className="flex justify-between items-end gap-3">
                    <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 p-2.5">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">TOTAL AMOUNT IN WORDS</span>
                        <p className="text-[13px] font-black text-slate-900 uppercase italic leading-tight">{data.details.amountInWords}</p>
                    </div>
                    <div className={`${theme.totalBg} rounded-xl p-3 text-white shadow-lg min-w-[260px]`}>
                        <div className="space-y-1.5">
                            <div className="flex justify-between text-[12px] font-bold text-white/70 uppercase">
                                <span>SUB-TOTAL</span>
                                <span className="text-white font-black">{formatCurrency(subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-[12px] font-bold text-white/70 uppercase">
                                <span>VAT {isVatNA ? '(N/A)' : `(${data.details.taxRate}%)`}</span>
                                <span className="text-white font-black">{formatCurrency(taxAmount)}</span>
                            </div>
                            <div className="pt-2 border-t border-white/20 mt-1 flex justify-between items-baseline">
                                <span className={`text-[14px] font-black ${theme.highlight} uppercase tracking-tight`}>
                                    {isKenya ? 'TOTAL INCL. VAT' : 'GRAND TOTAL'}
                                </span>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="text-[12px] font-bold text-white/40">{data.details.currency}</span>
                                    <span className="text-xl font-black">{formatCurrency(total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {data.details.notes && (
                    <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">SPECIAL NOTES</span>
                        <p className="text-[12px] text-slate-800 font-bold whitespace-pre-wrap leading-relaxed">{data.details.notes}</p>
                    </div>
                )}

                {/* Bank Details Section */}
                <div className="pt-1 border-t border-slate-100 px-2.5">
                    <h3 className="text-[15px] font-bold text-black mb-1.5">
                        <u>OUR BANK DETAILS:</u>
                    </h3>
                    <div className="flex flex-col gap-y-1">
                        {isTanzania ? (
                            <div className="space-y-1.5">
                                {(() => {
                                    const raw = data.business.bankDetails;
                                    const firstLine = raw.split('\n')[0];
                                    let accountName = "SHIV INTERNATIONAL LTD";
                                    if (firstLine.toLowerCase().includes('a/c name')) {
                                        accountName = firstLine.split(':')[1]?.trim().toUpperCase() || accountName;
                                    }

                                    const blocks = raw.split('\n').filter(l => l.trim() && !l.toLowerCase().includes('a/c name'));

                                    return (
                                        <>
                                            <p className="text-[16px] font-bold text-black uppercase tracking-tight mb-2">
                                                A/C NAME: {accountName}
                                            </p>
                                            {blocks.map((line, idx) => (
                                                <p key={idx} className="text-[13px] font-normal text-slate-900 leading-tight uppercase tracking-tight border-l-4 border-emerald-100 pl-3">
                                                    {line}
                                                </p>
                                            ))}
                                        </>
                                    );
                                })()}
                            </div>
                        ) : (
                            data.business.bankDetails.split('\n\n').filter(block => block.trim()).map((block, idx) => {
                                const lines = block.split('\n').filter(l => l.trim());
                                return (
                                    <div key={idx} className="space-y-1">
                                        {lines.map((line, lIdx) => (
                                            <p key={lIdx} className="text-[14px] font-normal text-slate-800 leading-tight uppercase tracking-tight">
                                                {line}
                                            </p>
                                        ))}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="flex justify-end mt-auto pt-16">
                    <div className="text-right inline-block">
                        <div className={`border-b border-slate-400 mb-2 w-48`}></div>
                        <p className="text-[11px] font-black text-slate-900 uppercase tracking-[0.2em] whitespace-nowrap">AUTHORISED SIGNATORY</p>
                    </div>
                </div>

                <div className="pt-0.5 border-t border-slate-50 flex justify-end items-center text-[7px] font-black text-slate-300 uppercase tracking-[0.4em]">
                    <span>PAGE 1 OF 1</span>
                </div>
            </div>
        </div>
    </div>
  );
};

export default QuotePreview;